import {
    BadgeCheck,
    Bell,
    ChevronsUpDown,
    CreditCard,
    LogOut,
    Settings,
    Sparkles,
    Plus,
    Loader2,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from "@/components/ui/sidebar";

import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { useImpersonationStore } from "@/stores/impersonationStore";
import { useShopStore } from "@/stores/shopStore";

export function ShopSwitcher({
    shops,
    activeShop,
    onShopChange,
    onAddNewShop,
}: {
    shops: { id: string; name: string; logo?: string }[];
    activeShop?: { id: string; name: string; logo?: string } | null;
    onShopChange: (shopId: string) => void;
    onAddNewShop?: () => void;
}) {
    const { isMobile } = useSidebar();
    const { session, signOut } = useAuth();
    const { resetImpersonation } = useImpersonationStore();
    const { resetShop } = useShopStore();

    const handleSignOut = () => {
        resetShop();
        resetImpersonation();
        signOut();
    };

    const emailToAvatarFallback = (email: string) => {
        return email
            ?.split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase();
    };

    const shopToAvatarFallback = (name: string) => {
        return name
            ?.split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase();
    };

    // Generate a deterministic gradient based on shop name
    const getGradientForShop = (name: string) => {
        // Generate a hash from the shop name
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }

        // Generate two colors from the hash
        const color1 = `hsl(${Math.abs(hash % 360)}, 70%, 60%)`;
        const color2 = `hsl(${Math.abs((hash * 2) % 360)}, 70%, 60%)`;

        // Return inline style for gradient
        return {
            background: `linear-gradient(to bottom right, ${color1}, ${color2})`,
        };
    };

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                            size="lg"
                            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                        >
                            {!activeShop ? (
                                <>
                                    <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-muted">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    </div>
                                    <div className="grid flex-1 text-left text-sm leading-tight">
                                        <span className="truncate font-semibold">
                                            Loading...
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Avatar className="h-8 w-8 rounded-lg">
                                        <AvatarImage alt={activeShop.name} />
                                        <AvatarFallback
                                            className="rounded-lg text-white"
                                            style={getGradientForShop(
                                                activeShop.name
                                            )}
                                        >
                                            {shopToAvatarFallback(
                                                activeShop.name
                                            )}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="grid flex-1 text-left text-sm leading-tight">
                                        <span className="truncate font-semibold">
                                            {activeShop.name}
                                        </span>
                                    </div>
                                </>
                            )}
                            <ChevronsUpDown className="ml-auto size-4" />
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                        side={isMobile ? "bottom" : "right"}
                        align="end"
                        sideOffset={4}
                    >
                        <DropdownMenuLabel>Shops</DropdownMenuLabel>
                        <DropdownMenuGroup>
                            {shops.map((shop) => (
                                <DropdownMenuItem
                                    key={shop.id}
                                    onClick={() => onShopChange(shop)}
                                    className="flex items-center gap-2"
                                >
                                    <Avatar className="h-6 w-6 rounded-md">
                                        <AvatarImage alt={shop.name} />
                                        <AvatarFallback
                                            className="rounded-md text-white text-xs"
                                            style={getGradientForShop(
                                                shop.name
                                            )}
                                        >
                                            {shopToAvatarFallback(shop.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    {shop.name}
                                    {activeShop &&
                                        shop.id === activeShop.id && (
                                            <BadgeCheck className="ml-auto h-4 w-4 text-primary" />
                                        )}
                                </DropdownMenuItem>
                            ))}

                            {onAddNewShop && (
                                <DropdownMenuItem
                                    onClick={onAddNewShop}
                                    className="flex items-center gap-2 text-primary"
                                >
                                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10">
                                        <Plus className="h-4 w-4" />
                                    </div>
                                    Add new shop
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuGroup>

                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Account</DropdownMenuLabel>
                        <DropdownMenuGroup>
                            <DropdownMenuItem className="flex items-center gap-2 cursor-default bg-muted/50 rounded-md mx-1 my-1 hover:bg-muted/50">
                                <Avatar className="h-6 w-6 rounded-md">
                                    <AvatarFallback className="rounded-md bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white text-xs">
                                        {emailToAvatarFallback(
                                            session?.user?.email ?? ""
                                        )}
                                    </AvatarFallback>
                                </Avatar>
                                <span className="truncate font-semibold">
                                    {session?.user?.email}
                                </span>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                                <Link
                                    to="/settings"
                                    className="flex items-center gap-2"
                                >
                                    <Settings className="h-4 w-4" />
                                    Settings
                                </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={handleSignOut}
                                className="flex items-center gap-2"
                            >
                                <LogOut className="h-4 w-4" />
                                Log out
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    );
}
